/** Switching between the three incident capture forms (audit-J D4, F11).
 * Shared answers (what happened, when, where, who) carry across in this tab only. Answers the
 * other form has no place for stay in the source form's own tab draft, so nothing is lost until
 * the tab closes. The transfer sits in sessionStorage, is keyed by provider and signed-in account,
 * and is consumed once. Capture is never blocked by any of this. */
export type CaptureTransfer = {
  v: 1;
  from: 'unified' | 'legacy' | 'whs';
  description?: string;
  location?: string;
  facilityId?: string;
  immediateActions?: string;
  /** Local 'YYYY-MM-DDTHH:mm' in the service location's time zone, when entered. */
  occurredLocal?: string;
  clientIds?: string[];
  writtenAt: number;
};

export const CAPTURE_TRANSFER_MAX_AGE_MS = 15 * 60 * 1000;
export const CAPTURE_TRANSFER_PREFIX = 'incident-capture-transfer:';
export const captureTransferKey = (tenantId?: string | null, actorId?: string | null) =>
  tenantId && actorId ? `${CAPTURE_TRANSFER_PREFIX}${tenantId}:${actorId}` : null;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const tabStore = (): Store | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

export function writeCaptureTransfer(
  key: string | null,
  transfer: Omit<CaptureTransfer, 'v' | 'writtenAt'>,
  store: Store | null = tabStore(),
  now = Date.now(),
): boolean {
  if (!key || !store) return false;
  try {
    store.setItem(key, JSON.stringify({ ...transfer, v: 1, writtenAt: now }));
    return true;
  } catch {
    return false;
  }
}

/** Read and remove. A stale, malformed or foreign value is dropped and reads as nothing. */
export function takeCaptureTransfer(
  key: string | null,
  store: Store | null = tabStore(),
  now = Date.now(),
): CaptureTransfer | null {
  if (!key || !store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    store.removeItem(key);
    const value = JSON.parse(raw) as CaptureTransfer;
    if (value?.v !== 1 || !Number.isFinite(value.writtenAt)) return null;
    if (now - value.writtenAt < 0 || now - value.writtenAt > CAPTURE_TRANSFER_MAX_AGE_MS)
      return null;
    return value;
  } catch {
    return null;
  }
}

export const hasTransferableAnswers = (t: Omit<CaptureTransfer, 'v' | 'writtenAt' | 'from'>) =>
  Boolean(
    t.description?.trim() ||
    t.location?.trim() ||
    t.immediateActions?.trim() ||
    t.occurredLocal ||
    t.clientIds?.length,
  );

export const SWITCH_FORM_WARNING =
  'Your description, time, place and the residents you chose will carry across to the other form. Other answers stay saved on this form in this tab, and come back if you return here, but closing the tab loses them. Any linked context, such as the record you started from, does not carry across. Switch forms?';

/** Confirms only when there is something to lose, then stages the transfer. */
export function confirmAndStageSwitch(options: {
  hasChanges: boolean;
  key: string | null;
  transfer: Omit<CaptureTransfer, 'v' | 'writtenAt'>;
  confirm?: (message: string) => boolean;
  store?: Store | null;
}): boolean {
  const ask = options.confirm ?? ((message: string) => window.confirm(message));
  if (options.hasChanges && !ask(SWITCH_FORM_WARNING)) return false;
  if (hasTransferableAnswers(options.transfer))
    writeCaptureTransfer(options.key, options.transfer, options.store);
  return true;
}

/** 'YYYY-MM-DD' + 'HH:mm' to the local datetime-local shape, or '' when either is missing. */
export const joinLocalDateTime = (date?: string, time?: string) =>
  date && time ? `${date}T${time}` : '';
export const splitLocalDateTime = (value?: string) => {
  const [date = '', time = ''] = (value ?? '').split('T');
  return { date, time: time.slice(0, 5) };
};

/** Sign-out: remove every staged transfer for every account in this tab. */
export function clearCaptureTransfers(
  storage: Pick<Storage, 'length' | 'key' | 'removeItem'>,
): void {
  const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
  for (const key of keys) if (key?.startsWith(CAPTURE_TRANSFER_PREFIX)) storage.removeItem(key);
}

/** Expiry sweep: drop stale or unreadable transfers whatever account staged them. */
export function sweepCaptureTransfers(
  storage: Pick<Storage, 'length' | 'key' | 'getItem' | 'removeItem'>,
  now = Date.now(),
): void {
  const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
  for (const key of keys) {
    if (!key?.startsWith(CAPTURE_TRANSFER_PREFIX)) continue;
    try {
      const value = JSON.parse(storage.getItem(key) ?? 'null') as CaptureTransfer | null;
      const age = now - Number(value?.writtenAt);
      if (value?.v !== 1 || !Number.isFinite(age) || age < 0 || age > CAPTURE_TRANSFER_MAX_AGE_MS)
        storage.removeItem(key);
    } catch {
      storage.removeItem(key);
    }
  }
}
